import $ from 'jquery';

import ArrayUtils from 'chairisher/util/array';
import ChoiceTreeAutocomplete from 'chairisher/component/autocomplete/choicetree';
import ObjectUtils from 'chairisher/util/object';
import Pill, { PillAddOnType } from 'chairisher/component/pill';
import StorageUtils from 'chairisher/util/storage';
import TooltipHelper from 'chairisher/view/helper/tooltip';

import { trackSuggestionApply, trackSuggestionCancel } from 'chairisher/analytics/components/suggestions';
import { isAuthenticated } from 'chairisher/context/auth';
import {
    getEditableSuggestionsLabel,
    getMetaObject,
    getProductObject,
    getSuggestionsLabel,
} from 'chairisher/context/listingflow';
import { getProductObjectValueByKey } from 'chairisher/context/product';
import { syncListAriaHidden } from 'chairisher/util/accessibility';
import { wrapTextWithParenthesis } from 'chairisher/util/format';
import { DataType } from 'chairisher/util/type';

/**
 * Abstract parent class that encapsulates common functionality for the sections of the Product listing flow.
 *
 * @abstract
 * @see AdminCard
 * @see BasicsCard
 * @see DetailsCard
 * @see LogisticsCard
 */
class ProductUpdateCard {
    constructor(cardSelector) {
        /**
         * Used to traverse the DOM to select the card
         *
         * @type {string}
         */
        this.cardSelector = cardSelector;

        this.cardFieldsSelector = '.js-section-fields';
        this.cardHeaderSelector = '.js-form-header';

        /**
         * Mapping of field name to ChoiceTreeAutocomplete instance
         *
         * @type {Object.<ChoiceTreeAutocomplete>}

         */
        this.fieldNameToAutocomplete = {};

        /**
         * jQuery instance of the card
         * @type {jQuery}
         */
        this.$cardEl = $(this.cardSelector);

        /**
         * jQuery instance of the card's fields container
         *
         * @type {jQuery}
         */
        this.$cardFieldsEl = this.$cardEl.children(this.cardFieldsSelector);

        /**
         * jQuery instance of the card's header element
         *
         * @type {jQuery}
         */
        this.$cardHeaderEl = this.$cardEl.children(this.cardHeaderSelector);

        /**
         * Actions to use when showing/hiding collapsable cards
         *
         * @enum {string}
         * @see https://getbootstrap.com/docs/3.3/javascript/#collapse
         */
        this.COLLAPSE_ACTION = {
            HIDE: 'hide',
            SHOW: 'show',
            TOGGLE: 'toggle',
        };

        /**
         * A collection of field names that are bound as ChoiceTreeAutocomplete components
         *
         * @type {Array.<string>}
         */
        this.choiceTreeComponentNames = [];

        /**
         * Used to traverse the DOM to select the card's completion indicator
         *
         * @type {string}
         */
        this.completionIndicatorSelector = '';

        /**
         * Collection of field names in this card, excluding buttons
         *
         * @type {Array.<string>}
         */
        this.fieldNames = [];

        /**
         * Flag that indicates if all required fields in this card are filled in
         *
         * @type {boolean}
         */
        this.isComplete = true;

        /**
         * Flag that indicates if this card contains any errors
         *
         * @type {boolean}
         */
        this.hasErrors = false;

        /**
         * Flag that indicates if this card contains any warnings
         *
         * @type {boolean}
         */
        this.hasWarnings = false;

        /**
         * A collection of field names that are bound as BooleanInput components
         *
         * @type {Array.<string>}
         */
        this.booleanInputComponentNames = [];

        /**
         * A collection of field names that are bound as ImageSelect components
         *
         * @type {Array.<string>}
         */
        this.imageSelectComponentNames = [];

        /**
         * A collection of field names that are bound as MultiSelectCheckbox components
         *
         * @type {Array.<string>}
         */
        this.multiSelectCheckboxComponentNames = [];

        /**
         * Relational object pairing the selected parent field with the targeted parent field, as well as their input values
         *
         * @type {Object.<string, string>}
         */
        this.selectedDisabledInputs = {};

        /**
         * A collection of objects whose nested values represent the parent field names to be targeted
         * The child inputs of those parent fields will later have their disabled inputs cleared based on the selection of the key
         *
         * @type {Array.<object>}
         */
        this.selectedDisabledResets = [];

        /**
         * A collection of objects with the parent key representing the initiating field section
         * The nested object displays the relationship between the parent's input and a separate, targeted field
         *
         * @type {Array.<object>}
         */
        this.selectedFieldsByFieldInput = [];

        /**
         * A collection of objects with the key representing the initiating field section
         * The object's values represent separate fields to take action upon
         *
         * @type {Array.<object>}
         */
        this.selectedFieldsByFieldName = [];

        /**
         * Mapping of parent field names to child input value to use for when to initiate action upon other fields
         *
         * @type {Object.<string, string>}
         */
        this.selectedFieldsByFieldNameKeyMap = {};

        /**
         * Relational object pairing the selected parent field with the targeted parent field, as well as their input values
         *
         * @type {Object.<string, string>}

         */
        this.selectedHiddenInputs = {};

        /**
         * A collection of field names that can be used to select simple fields
         *
         * @type {Array.<string>}
         */
        this.simpleFieldNames = [];
    }

    /**
     * Format the single select datum properly
     *
     * @param {object} Data of the field name received from the server
     * @returns {object} Data formatted the right way so that the widget works on the frontend
     *
     */
    generateSingleSelectDatum(datum) {
        const newDatum = {};
        $.each(['errors', 'suggestion', 'is_readonly', 'is_required', 'readonly_text'], (index, field) => {
            if (Object.prototype.hasOwnProperty.call(datum, field)) {
                newDatum[field] = datum[field];
            }
        });
        if (datum.value) {
            const newValueObj = {};
            $.each(['taxonomy', 'value', 'display'], (index, field) => {
                if (Object.prototype.hasOwnProperty.call(datum, field)) {
                    newValueObj[field] = datum[field];
                }
            });
            newDatum.value = [newValueObj];
        }
        return newDatum;
    }

    /**
     * @returns {jQuery} jQuery instance of the card
     */
    getCardEl() {
        return this.$cardEl;
    }

    /**
     * @returns {boolean} True indicates there are errors in this card, false otherwise
     */
    hasErrors() {
        return this.hasErrors;
    }

    /**
     * Sanitizes field value and triggers a change event
     *
     * @param {jQuery} $el The element whose value is changing
     */
    sanitizeAndTriggerSimpleFieldChange($el) {
        const key = $el.attr('name');
        let val = $el.val();

        // attempt to parse numbers as long as it's not an empty string
        // so we don't pass NaN up to the server...
        const type = $el.attr('type');

        if (type === DataType.NUMBER) {
            if (val === '') {
                val = null;
            } else {
                val = parseFloat(val);
            }
        } else if (type === 'checkbox') {
            if (val === 'on') {
                val = $el.is(':checked');
            }
        }

        const dataObj = {};
        dataObj[key] = val;

        // bypasses boolean inputs and multi select checkboxes, as they contain own triggers, so avoids duplicate triggers
        if (!this.booleanInputComponentNames.includes(key) && !this.multiSelectCheckboxComponentNames.includes(key)) {
            this.triggerChange(dataObj);
        }
    }

    /**
     * Updates the appearance of the completion indicators related to this card
     */
    updateCompletionIndicators() {
        if (this.completionIndicatorSelector) {
            const $indicators = $(this.completionIndicatorSelector);
            $indicators.removeClass('complete, error, warning');
            if (this.hasErrors) {
                $indicators.addClass('error');
            } else if (this.isComplete) {
                $indicators.addClass('complete');
            }
        }
    }

    /**
     * Updates the current values and visibility of this card's simple fields with a given set of data.
     *
     * @param {Object} data The data to update the fields with
     */
    updateFieldsFromDataObject(data) {
        //
        // Simple Fields
        //

        const $simpleFields = this.getSimpleFields();
        const fieldSelectorsToHide = [];
        const fieldSelectorsToShow = [];

        const updateVisibilitySelector = (datum, fieldName) => {
            const arr = datum === undefined ? fieldSelectorsToHide : fieldSelectorsToShow;
            arr.push(`.js-form-group.${fieldName}`);
        };

        const updateSelectChoices = ($el, choices, selectedValue) => {
            const options = [];
            for (let i = 0; i < choices.length; i += 1) {
                const choice = choices[i];
                options.push(`<option value="${choice.value}">${choice.display}</option>`);
            }
            $el.html(options.join(''));
            $el.val(selectedValue);
        };

        this.$cardFieldsEl.find('.form-group.has-error').removeClass('has-error');
        this.$cardFieldsEl.find('.form-group.has-warning').removeClass('has-warning');
        this.$cardFieldsEl.find('.js-errors').html('');

        this.hasErrors = false;
        this.hasWarnings = false;
        this.isComplete = true;

        $.each(this.fieldNames, (_, fieldName) => {
            const datum = data[fieldName];

            const hasData = datum && datum.value !== undefined;
            const isSimpleField = $.inArray(fieldName, this.simpleFieldNames) !== -1;

            // ensure simple fields are visible if the key is present in data...
            if (isSimpleField) {
                updateVisibilitySelector(datum, fieldName);
            }

            // select all fields with the same name...
            const $el = $simpleFields.filter(`[name="${fieldName}"]`);

            // ensure the field is enabled/disabled per the data, but excluding exceptions
            const disabledExceptions = this.multiSelectCheckboxComponentNames.concat(
                ObjectUtils.getAllNestedValues(this.selectedDisabledResets),
            );
            if (datum && !disabledExceptions.includes(fieldName)) {
                $el.prop('disabled', !!(datum && datum.is_readonly));
                this.updateFieldReadonlyText(fieldName, datum.readonly_text);
            }

            // if the field is a simple field and there is a value to set, we'll attempt to set it...
            if (isSimpleField && hasData) {
                const { value } = datum;

                if (value === true || value === false) {
                    // if the value is true or false, we know it's a checkbox or radio button...
                    $el.prop('checked', value);
                } else if ($el.length > 1) {
                    // if the value isn't true or false and there are multiple fields,
                    // we know it's still a checkbox or radio button, so we need to check the right one...
                    $el.filter(`[value="${value}"]`).prop('checked', true);

                    // if value is an array, we know there are multiple checkboxes to be selected...
                    if (Array.isArray(value)) {
                        value.forEach((obj) => {
                            if (Object.prototype.hasOwnProperty.call(obj, 'value')) {
                                $el.filter(`[value="${obj.value}"]`).prop('checked', true);
                            }
                        });
                    }
                } else {
                    // otherwise it's a textbox, textarea, or select element so set the value...
                    $el.val(value);
                }
            }

            // update errors, warnings, help_text, labels, suggestions, and completion status...
            if (datum) {
                if (datum.errors) {
                    this.hasErrors = true;
                    this.updateFieldErrors(fieldName, datum.errors);
                } else if (datum.warnings) {
                    this.hasWarnings = true;
                    $(`.form-group.${fieldName}`).addClass('has-warning');
                } else if (datum.is_required && !hasData) {
                    this.isComplete = false;
                }

                if (datum.help_text) {
                    this.updateFieldHelpText(fieldName, datum.help_text);
                }

                if (datum.label) {
                    this.updateFieldLabel(fieldName, datum.label);
                }

                if (datum.is_variant) {
                    this.getFieldFormGroup(fieldName)
                        .closest('.js-variant-attribute-group')
                        .addClass('variant-attribute-group');
                }

                // update select choices
                if (
                    $el.length === 1 &&
                    $el.is('select') &&
                    ['pickup_state', 'unit_type', 'primary_stone'].indexOf(fieldName) !== -1 // remove this condition when we stop setting other field choices in the context dict
                ) {
                    if (datum.choices) {
                        updateSelectChoices($el, datum.choices, datum.value);
                    } else if (datum.choices_url) {
                        let storedChoices = [];

                        const storedChoicesJson = StorageUtils.getItem(datum.choices_url);
                        if (storedChoicesJson) {
                            storedChoices = JSON.parse(storedChoicesJson);
                            updateSelectChoices($el, storedChoices, datum.value);
                        } else if (isAuthenticated()) {
                            // The endpoints pointed to by `choices_url` require authentication
                            $.ajax({
                                type: 'GET',
                                url: datum.choices_url,
                            }).done((choicesResponse) => {
                                storedChoices = choicesResponse.tree;
                                if (storedChoices) {
                                    StorageUtils.setItem(datum.choices_url, JSON.stringify(storedChoices));
                                    updateSelectChoices($el, storedChoices, datum.value);
                                }
                            });
                        }
                    }
                }
            }
        });

        //
        // Autocomplete Fields
        //

        $.each(this.choiceTreeComponentNames, (_, fieldName) => {
            const autocomplete = this.fieldNameToAutocomplete[fieldName];
            let datum = data[fieldName];
            if (autocomplete.isSingleSelect) {
                datum = this.generateSingleSelectDatum(datum);
            }
            const hasData = datum && datum.value;

            // ensure Autocomplete fields are visible if they are present in the data...
            updateVisibilitySelector(datum, fieldName);

            if (hasData) {
                for (let i = 0; i < datum.value.length; i += 1) {
                    const choice = datum.value[i];
                    if (!autocomplete.hasChoice(choice.value)) {
                        autocomplete.addChoice(choice);
                    }
                }
            }

            if (autocomplete) {
                if (datum && datum.is_readonly) {
                    autocomplete.disable();
                } else {
                    autocomplete.enable();
                }
            }
        });

        $(fieldSelectorsToHide.join(',')).addClass('hidden');
        $(fieldSelectorsToShow.join(',')).removeClass('hidden');
    }

    /**
     * Updates the various fieldsets with any instructions that need to be displayed.
     */
    updateInstructionsFromDataObject() {}

    /**
     * Updates the various fields with any warnings they might have received.
     */
    updateWarningsFromDataObject() {}

    /**
     * Updates the current values and visibility of this card's fields that display meta data.
     */
    updateMetaFieldsFromDataObject() {}

    /**
     * Binds actions to the various elements that comprise the Card
     */
    bind() {
        //
        // intercept, sanitize, and re-trigger simple field change events
        //

        this.getSimpleFields().on('change', (e) => {
            this.sanitizeAndTriggerSimpleFieldChange($(e.target));
        });

        //
        // add suggestions
        //

        this.$cardFieldsEl.on('mousedown', '.js-suggestion', (e) => {
            e.preventDefault();
            e.stopPropagation(); // don't let the event bubble and open up the autocomplete widget...

            const $suggestion = $(e.currentTarget);

            const choice = $.extend({}, $suggestion.data());
            const { fieldName } = choice;
            delete choice.fieldName;

            this.addSuggestion(fieldName, choice);
            trackSuggestionApply($suggestion.data(), this.getMetaObject().id);
            this.removeSuggestionEl(fieldName, choice);
        });

        //
        // bulk add suggestions
        //

        this.$cardFieldsEl.on('click', '.js-bulk-add-keywords', (e) => {
            e.preventDefault();
            const $suggestions = $(e.target).closest('.js-form-suggestions').find('.js-suggestion');
            $.each($suggestions, (i) => {
                const $suggestion = $suggestions.eq(i);
                const choice = $.extend({}, $suggestion.data());
                const { fieldName } = choice;
                delete choice.fieldName;
                this.addSuggestion(fieldName, choice);
                trackSuggestionApply($suggestion.data(), this.getMetaObject().id, true);
                this.removeSuggestionEl(fieldName, choice);
            });
        });
    }

    /**
     * Adds a selection to the element the data has been suggested for.
     *
     * @param {string} fieldName The name of the field the suggestion has been made for
     * @param {Choice} choice A Choice datastructure containing the data representing the suggestion
     */
    addSuggestion(fieldName, choice) {
        const autocomplete = this.fieldNameToAutocomplete[fieldName];
        if (autocomplete) {
            if (autocomplete.isSingleSelect) {
                autocomplete.removeAllChoices();
            }
            autocomplete.addChoice(choice);

            const changeData = {};
            changeData[fieldName] = autocomplete.getValue();
            this.triggerChange(changeData);
        } else {
            const $simpleField = this.getSimpleFields().filter(`[name="${fieldName}"]`);
            if ($simpleField) {
                $simpleField.val(choice.value);
            }
        }
    }

    /**
     * Binds actions to a ChoiceTreeAutocomplete component with the given settings
     *
     * @param {Object} settings
     * @param {boolean=} settings.areOnlyLeafNodesSelectable True indicates only leaf nodes can be selected
     * @param {string=} settings.dataDisplayKey String that indicates which piece of data should be displayed in the menu
     * @param {string} settings.dataEndpoint URL where autocomplete suggestions should be pulled from
     * @param {boolean=} settings.menuChoiceHasThumbnails True indicates each menu choice will display a thumbnail
     * @param {Array.<Choice>=} settings.treeChoices Collection of Choice objects to display as a tree
     * @param {jQuery} settings.$queryEl The jQuery element to bind
     *
     */
    bindChoiceTreeAutocomplete(settings) {
        const options = $.extend(
            {
                areOnlyLeafNodesSelectable: false,
                dataDisplayKey: 'taxonomy',
                dataEndpoint: undefined,
                menuChoiceHasThumbnail: false,
                treeChoices: undefined,
                $queryEl: undefined,
            },
            settings,
        );

        const autocomplete = new ChoiceTreeAutocomplete(options);

        if (options.treeChoices) {
            autocomplete.getMenu().updateTreeChoices(options.treeChoices);
        }

        const { $queryEl } = options;
        const fieldName = $queryEl.attr('name');
        this.fieldNameToAutocomplete[fieldName] = autocomplete;

        $queryEl.on('choicetree:change', (e, value) => {
            const changeData = {};
            changeData[e.target.name] = value;

            this.triggerChange(changeData);
        });
    }

    /**
     * Binds actions to BooleanInput components with the given settings
     *
     * @param {object} settings
     * @param {jQuery} settings.$queryEl The jQuery element to bind
     *

     */
    bindBooleanInputs(settings) {
        const options = $.extend(
            {
                $queryEl: undefined,
            },
            settings,
        );

        const { $queryEl } = options;
        const fieldName = $queryEl.attr('name');

        // ensures any persisted disabled/hidden actions from previously saved listing are established at load
        const existingValue = getProductObjectValueByKey(fieldName);
        if (existingValue) {
            this.toggleDisabledInputs(existingValue, fieldName);
            this.toggleHiddenInputs(existingValue, fieldName);
        }

        $queryEl.on('change', (e) => {
            let changeData = {};

            const childFields = ObjectUtils.getObjValuesByFieldName(this.selectedFieldsByFieldName, fieldName);
            if (e.target.value === this.selectedFieldsByFieldNameKeyMap[fieldName]) {
                changeData = this.resetChildFieldsData(changeData, childFields);
            }

            this.clearSelectedBooleanInputUi(e.target.value, fieldName, childFields);
            this.toggleDisabledInputs(e.target.value, fieldName);
            const fieldsWithHiddenInput = this.toggleHiddenInputs(e.target.value, fieldName);
            fieldsWithHiddenInput.forEach((field) => {
                changeData[field] = '';
            });

            changeData[e.target.name] = e.target.value;
            this.triggerChange(changeData);
        });
    }

    /**
     * Binds actions to ImageSelect components with the given settings
     *
     * @param {object} settings
     * @param {jQuery} settings.$queryEl The jQuery element to bind
     *

     */
    bindImageSelectComponentNames(settings) {
        const options = $.extend(
            {
                $queryEl: undefined,
            },
            settings,
        );

        const { $queryEl } = options;
        const fieldName = $queryEl.attr('name');

        $queryEl.on('change', () => {
            const imageSelectFieldText = this.getImageSelectFieldText(fieldName);
            this.setImageSelectFieldText(fieldName, imageSelectFieldText);
            this.setImageSelectedActiveState(fieldName);
        });
    }

    /**
     * Binds actions to MultiSelectCheckbox components with the given settings
     *
     * @param {object} settings
     * @param {jQuery} settings.$queryEl The jQuery element to bind
     */
    bindMultiSelectCheckboxes(settings) {
        const options = $.extend(
            {
                $queryEl: undefined,
            },
            settings,
        );

        const { $queryEl } = options;
        const fieldName = $queryEl.attr('name');
        let arr = [];

        // takes into account the saved, existing values, which need to be considered, due to the multi selects ability to deselect
        const existingValue = getProductObjectValueByKey(fieldName);
        if (existingValue) {
            if (Array.isArray(existingValue)) {
                existingValue.forEach((obj) => {
                    if (Object.prototype.hasOwnProperty.call(obj, 'value')) {
                        arr.push(obj.value);
                    }
                });
            } else {
                arr.push(existingValue);
            }
        }

        // ensures any persisted disabled actions from previously saved listing are established at load
        if (arr.includes(this.selectedFieldsByFieldNameKeyMap[fieldName])) {
            $(`[name="${fieldName}${'"]'[0]}`).prop('checked', true);
            const childFields = ObjectUtils.getObjValuesByFieldName(this.selectedFieldsByFieldName, fieldName);
            this.clearSelectedCheckboxUi(this.selectedFieldsByFieldNameKeyMap[fieldName], fieldName, childFields);
        }

        $queryEl.on('change', (e) => {
            let changeData = {};
            const selectionIdx = parseInt(e.target.id.slice(-1), 10);

            arr = ArrayUtils.addOrRemoveArrayItem(arr, e.target.value);
            if (e.target.value === this.selectedFieldsByFieldNameKeyMap[fieldName]) {
                arr = this.setCheckboxArrayToValue(selectionIdx, arr, e.target.value, fieldName);
                const childFields = ObjectUtils.getObjValuesByFieldName(this.selectedFieldsByFieldName, fieldName);
                changeData = this.resetChildFieldsData(changeData, childFields);
                this.clearSelectedCheckboxUi(e.target.value, fieldName, childFields);
            }

            const fieldInputs = ObjectUtils.getChildKeysByFieldName(this.selectedFieldsByFieldInput, fieldName);
            if (fieldInputs.includes(e.target.value)) {
                const childFields = ObjectUtils.getChildValuesByFieldName(
                    this.selectedFieldsByFieldInput,
                    fieldName,
                    e.target.value,
                );
                changeData = this.resetChildFieldsData(changeData, childFields);
                this.clearMultipleFieldsSelections(childFields);
            }

            changeData[e.target.name] = arr;
            this.triggerChange(changeData);
        });
    }

    /**
     * Binds actions to suggestion Pills.
     *
     * @param {Pill} pill The Pill object whose HTML element should be bound
     */
    bindSuggestionPill(pill) {
        pill.getEl().on('pill:remove', (e, pillValue) => {
            const $pill = $(e.target);
            this.removeSuggestionEl($pill.data('field-name'), pillValue, false);
            trackSuggestionCancel($pill.data(), this.getMetaObject().id);
        });
    }

    /**
     * Handles the resetting of the disabled inputs of the specified parent field(s)
     *
     * @param {string} val The target value of the event listener
     * @param {string} fieldName The name of the parent group of input items (e.g. alterations_code)

     */
    clearDisabledInputs(val, fieldName) {
        const childFields = ObjectUtils.getChildValuesByFieldName(this.selectedDisabledResets, fieldName, val);
        childFields.forEach((childField) => {
            const $selector = $(`.${childField}`);
            $selector.find('input').prop('disabled', false);
            $selector.find('label').removeClass('disabled');
        });
    }

    /**
     * Clears any input or option selections for all of the the provided child fields.
     *
     * @param {array} childFields The list of corresponding value fields whose selections will be cleared

     */
    clearMultipleFieldsSelections(childFields) {
        childFields.forEach((field) => {
            const $selector = $(`.${field}`);
            this.clearSingleFieldSelections($selector);
        });
    }

    /**
     * Actions to take against the UI if the specified boolean input is selected.
     * Handles the clearing of input or option selections, as well as disabled attributes
     *
     * @param {string} val The target value of the event listener
     * @param {string} fieldName The name of the parent group of input items (e.g. used_code)
     * @param {array} childFields The list of corresponding value fields whose inputs will be cleared

     */
    clearSelectedBooleanInputUi(val, fieldName, childFields) {
        if (childFields.length > 0) {
            if (val === this.selectedFieldsByFieldNameKeyMap[fieldName]) {
                this.clearMultipleFieldsSelections(childFields);
                this.clearDisabledInputs(val, fieldName);
            }
        }
    }

    /**
     * Actions to take against the UI if the specified checkbox is selected.
     * Handles removing checked values, enabling/disabling checkbox fields and clearing other input values.
     *
     * @param {string} val The target value of the event listener
     * @param {string} fieldName The name of the parent group of checkbox items (e.g. power_sources)
     * @param {array} childFields The list of corresponding value fields whose inputs will be cleared

     */
    clearSelectedCheckboxUi(val, fieldName, childFields) {
        const $allCheckboxes = $(`[name="${fieldName}"]`);
        const $specifiedCheckbox = $($allCheckboxes[0]);
        const $restOfCheckboxes = $allCheckboxes.slice(1);

        if ($specifiedCheckbox.prop('checked')) {
            $restOfCheckboxes.prop('checked', false);
            $restOfCheckboxes.prop('disabled', true);
            this.clearMultipleFieldsSelections(childFields);
            this.clearDisabledInputs(val, fieldName);
        } else {
            $restOfCheckboxes.prop('disabled', false);
        }
    }

    /**
     * Clears any input or option selections for the provided child field.
     *
     * @param {jQuery} $selector The selector representing the child field whose selections are to be cleared

     */
    clearSingleFieldSelections($selector) {
        $selector.find('input').prop('checked', false);
        $selector.find('option:selected').prop('selected', false);
        $selector.find('.js-boolean-list-item').removeClass('selected');
    }

    /**
     * Locates the selected input from the targeted field section and returns the input's displayed text
     *
     * @param {string} fieldName The name of the field section which contains the input being targeted (e.g. plug_type)
     * @returns {string} The selected input's displayed text or a unique message to choose an option

     */
    getImageSelectFieldText(fieldName) {
        const $selectedInput = $(`input[name=${fieldName}]:checked`, `.${fieldName}`);
        const $booleanInput = $selectedInput.closest('.js-boolean-input');
        const $title = $booleanInput.find('.js-label-title');
        const $subtitle = $booleanInput.find('.js-label-subtitle');

        let displayText = '';
        if ($title.length) {
            displayText += $title.text();

            if ($subtitle.length) {
                displayText += wrapTextWithParenthesis($subtitle.text());
            }

            return displayText;
        }

        return `Choose ${this.imageSelectLabelDisplayMap[fieldName]}`;
    }

    /**
     * @param {string} fieldName The name of the field whose form group we want to find
     * @returns {jQuery} Form group for the specified field

     */
    getFieldFormGroup(fieldName) {
        return this.$cardFieldsEl.find(`.form-group.${fieldName}`);
    }

    /**
     * @returns {Object} A datastructure used to represent "meta" information for the listing flow
     */
    getMetaObject() {
        return getMetaObject();
    }

    /**
     * @returns {Object} A datastructure used to represent "product" information for the listing flow
     */
    getProductObject() {
        return getProductObject();
    }

    /**
     * @returns {jQuery} Collection of simple fields that should be bound

     */
    getSimpleFields() {
        return this.$cardFieldsEl.find(this.simpleFieldNames.map((fieldName) => `[name="${fieldName}"]`).join(', '));
    }

    /**
     * Calls all the necessary internal functions. It's up to subclasses to call this to avoid order of operations
     * issues between constructors.
     *

     */
    initialize() {
        this.populateFieldNames();
        this.populateSimpleFieldNames();

        this.bind();
    }

    /**
     * Populates the list of field names in this card, excluding buttons
     *

     */
    populateFieldNames() {
        const fieldNames = [];
        const $fields = this.$cardEl.find(
            'button[name][name!=""],input[name][name!=""],select[name][name!=""],textarea[name][name!=""]',
        );

        $.each($fields, (i, el) => {
            const { name } = el;
            if (name && $.inArray(name, fieldNames) === -1) {
                fieldNames.push(el.name);
            }
        });

        this.fieldNames = fieldNames;
    }

    /**
     * Populates the list of simple field names excluding buttons
     *

     */
    populateSimpleFieldNames() {
        this.simpleFieldNames = [].concat(this.fieldNames);
    }

    /**
     * Removes a suggestion element (either a tag, or a form-group "link") from the ui.
     *
     * @param {string} fieldName The name of the field the suggestion has been made for
     * @param {Choice} choice A Choice datastructure containing that represents the suggestion
     * @param {boolean=} shouldRemoveFormGroupSuggestion False indicates a form-group suggestion can be removed. Defaults to true.
     */
    removeSuggestionEl(fieldName, choice, shouldRemoveFormGroupSuggestion) {
        const $suggestions = $(`.js-suggestion[data-field-name="${fieldName}"][data-value="${choice.value}"]`);

        $.each($suggestions, (i) => {
            const $suggestion = $suggestions.eq(i);
            let $parent = $suggestion.parent();

            if ($parent.find('.js-suggestion').length === 1) {
                if ($parent.hasClass('js-pill-container')) {
                    $parent = $parent.closest('.js-form-suggestions');
                }

                $parent.addClass('hidden');
            }

            if ($suggestion.hasClass('js-pill') || shouldRemoveFormGroupSuggestion !== false) {
                $suggestion.remove();
            }
        });
    }

    /**
     * Resets the values of the corresponding child fields. Also, clears text field for image select sections.
     * This is typically performed due to the fields being hidden.
     *
     * @param {object} changeData The data object to be updated
     * @param {array} childFields The list of corresponding value fields whose data will be reset
     * @returns {object} The updated version of the object

     */
    resetChildFieldsData(changeData, childFields) {
        const changeDataCopy = { ...changeData };
        childFields.forEach((value) => {
            changeDataCopy[value] = '';

            if (this.imageSelectComponentNames.includes(value)) {
                const imageSelectFieldText = this.getImageSelectFieldText(value);
                this.setImageSelectFieldText(value, imageSelectFieldText);
            }
        });

        return changeDataCopy;
    }

    /**
     * If checkbox is checked, clears array and adds passed value as a single element. If not, returns array unaltered.
     *
     * @param {number} idx The index of the selected checkbox option
     * @param {array} arr The array to be updated, if the selected checkbox is checked; otherwise, no change
     * @param {string} val The value of the selected checkbox item (e.g. NONE)
     * @param {string} fieldName The name of the parent group of checkbox items (e.g. power_sources)
     * @returns {array} The updated version of the array, if the selected checkbox is checked; otherwise, original

     */
    setCheckboxArrayToValue(idx, arr, val, fieldName) {
        const $allCheckboxes = $(`[name="${fieldName}"]`);
        const $selectedCheckbox = $($allCheckboxes[idx]);

        if ($selectedCheckbox.prop('checked')) {
            return [val];
        }

        return arr;
    }

    /**
     * Locates the preferred text container and populates it with the specified text
     *
     * @param {string} fieldName The name of the field section which contains the text container being targeted (e.g. plug_type)
     * @param {string} text The text to display in the specified text container

     */
    setImageSelectFieldText(fieldName, text) {
        const fieldNameKebabCase = fieldName.replace(/_/g, '-');
        const $textContainer = $(`#${fieldNameKebabCase}-field-text-container`);
        $textContainer.text(text);
    }

    /**
     * Adds styling to the selected image
     *
     * @param {string} fieldName The name of the field section which contains the input being targeted (e.g. plug_type)

     */
    setImageSelectedActiveState(fieldName) {
        const $listItems = $(`.${fieldName}`).find('.js-boolean-list-item');
        $listItems.removeClass('selected');

        const $selectedInput = $(`input[name=${fieldName}]:checked`, `.${fieldName}`);
        $selectedInput.closest('.js-boolean-list-item').addClass('selected');
    }

    /**
     * Handles disabling specific inputs of another field, based on the selected input of the parent field
     *
     * @param {object} val The target value of the event listener
     * @param {string} fieldName The name of the parent group of input items (e.g. alterations_code)

     */
    toggleDisabledInputs(val, fieldName) {
        const targetObj = this.selectedDisabledInputs[fieldName];
        const targetFields = targetObj ? Object.keys(targetObj) : [];
        if (targetObj && targetFields.length > 0) {
            targetFields.forEach((targetField) => {
                const targetFieldValues = Object.keys(targetObj[targetField]);
                targetFieldValues.forEach((targetFieldValue) => {
                    const inputValues = targetObj[targetField][targetFieldValue] || [];
                    const $targetOption = $(`.${targetField}`).find(`[value="${targetFieldValue}"]`);
                    if (inputValues.includes(val)) {
                        $targetOption.prop('disabled', true);
                        $targetOption.closest('label').addClass('disabled');
                    } else {
                        $targetOption.prop('disabled', false);
                        $targetOption.closest('label').removeClass('disabled');
                    }
                });
            });
        }
    }

    /**
     * Handles hiding specific inputs of another field, based on the selected input of the parent field
     *
     * @param {object} val The target value of the event listener
     * @param {string} fieldName The name of the parent group of input items (e.g. imperfections_code)
     * @returns {Array.<string>} Array of field names, if any of their selected inputs were hidden

     */
    toggleHiddenInputs(val, fieldName) {
        const targetObj = this.selectedHiddenInputs[fieldName];
        const fieldsWithSelectedInputHidden = [];
        if (targetObj && Object.keys(targetObj).length > 0) {
            const targetField = Object.keys(targetObj)[0];
            const inputValues = targetObj[targetField] ? Object.keys(targetObj[targetField]) : [];
            inputValues.forEach((inputValue) => {
                const $targetOption = $(`.${targetField}`).find(`[value="${targetObj[targetField][inputValue]}"]`);
                if (val === inputValue) {
                    $targetOption.closest('.js-boolean-list-item').addClass('hidden');
                    if ($targetOption.prop('checked')) {
                        $targetOption.prop('checked', false);
                        fieldsWithSelectedInputHidden.push(targetField);
                    }
                } else {
                    $targetOption.closest('.js-boolean-list-item').removeClass('hidden');
                }
            });
        }
        return fieldsWithSelectedInputHidden;
    }

    /**
     * Triggers a new event while passing along any other additional data provided
     *
     * @param {string} type The new event's type
     * @param {Object=} data Additional data to be passed along as an argument to the event handler

     */
    trigger(type, data) {
        this.$cardEl.trigger($.Event(type), data);
    }

    /**
     * Triggers the `card:change` event while passing along any other additional data provided
     *
     * @param {Object=} data Additional data to be passed along as an argument to the event handler

     */
    triggerChange(data) {
        this.trigger('card:change', data);
    }

    /**
     * Updates the errors displayed for a given field
     *
     * @param {string} fieldName The name of the field to display errors for
     * @param {Array.<Choice>} fieldErrors The errors to display

     */
    updateFieldErrors(fieldName, fieldErrors) {
        const $errorList = $('<ul></ul>', {
            class: 'errorlist',
        });

        for (let i = 0; i < fieldErrors.length; i += 1) {
            $errorList.append(
                $('<li></li>', {
                    text: fieldErrors[i].display,
                }),
            );
        }

        const $el = this.getFieldFormGroup(fieldName);
        $el.addClass('has-error');
        $el.find('.js-errors').html($errorList);
        syncListAriaHidden($errorList[0]);
    }

    /**
     * Updates the help text displayed for a given field
     *
     * @param {string} fieldName The name of the field to display help text for
     * @param {string} helpText The text to display as the element's help text

     */
    updateFieldHelpText(fieldName, helpText) {
        this.getFieldFormGroup(fieldName).find('.help-text').text(helpText);
    }

    /**
     * Updates the label displayed for a given field
     *
     * @param {string} fieldName The name of the field to display errors for
     * @param {string} label The text to display as the element's label

     */
    updateFieldLabel(fieldName, label) {
        const $controlLabel = this.getFieldFormGroup(fieldName).find('.control-label');
        const $optionalEl = $controlLabel.find('.js-optional').clone();
        const $fragment = $('<div></div>', {
            text: label,
        }).append($optionalEl);
        $controlLabel.html($fragment.html());
    }

    /**
     * Updates the read-only text displayed for a given field
     *
     * @param {string} fieldName The name of the field to display read-only text for
     * @param {string} readonlyText The text to display as the element's read-only text

     */
    updateFieldReadonlyText(fieldName, readonlyText) {
        const lockCssClass = 'readonly-tooltip';
        const $fieldFormGroup = this.getFieldFormGroup(fieldName);
        let $lock = $fieldFormGroup.find(`.${lockCssClass}`);
        if (readonlyText && !$lock.length) {
            $lock = $('<span></span>', {
                'class': `cicon cicon-lock-fill ${lockCssClass}`,
                'data-toggle': 'tooltip',
                'title': readonlyText,
            });
            $fieldFormGroup.prepend($lock);
            $lock.tooltip({ placement: TooltipHelper.getPlacement });
        } else if (!readonlyText && $lock.length) {
            $lock.tooltip('dispose');
            $lock.remove();
        }
    }

    /**
     * Updates the warnings displayed for a given field
     *
     * @param {jQuery} $fieldEl The field whose warnings should be updated
     * @param {Array.<Object>} warnings A list of warning objects to display
     */
    updateFieldWarnings($fieldEl, warnings) {
        const $warningListEl = $fieldEl.find('.js-warning-list');

        if (!warnings || !warnings.length) {
            $warningListEl.empty();
            syncListAriaHidden($warningListEl[0]);
            $fieldEl.addClass('hidden');
        } else {
            const warningEls = [];
            $.each(warnings, (i, obj) => {
                warningEls.push(
                    $('<li></li>', {
                        text: obj.display,
                    }),
                );
            });

            $warningListEl.html(warningEls);
            syncListAriaHidden($warningListEl[0]);
            $fieldEl.removeClass('hidden');
        }
    }

    /**
     * Adds auto suggestions to the ui under each applicable field.
     *
     * @param {Object} data The global product object that the listing flow is based on
     */
    updateSuggestions(data) {
        const fieldNameToSuggestionMap = {};

        $.each(this.fieldNames, (_, fieldName) => {
            const datum = data[fieldName];
            const suggestion = datum && datum.suggestion; // note: can be an object for a singular suggestion or an array for multiple

            if (suggestion && Array.isArray(suggestion)) {
                fieldNameToSuggestionMap[fieldName] = suggestion;
            }
        });

        // clean up fields without suggestions
        const selectors = Object.keys(fieldNameToSuggestionMap).map((k) => `.form-group.${k}`);

        const $elsWithoutSuggestions = this.$cardEl.find('.form-group').not(selectors.join(','));
        $.each($elsWithoutSuggestions, (i) => {
            $elsWithoutSuggestions.eq(i).find('.js-form-group-suggestions').remove();
        });

        const $formSuggestions = this.$cardEl.find('.js-form-suggestions');
        const $formSuggestionsPillContainer = $formSuggestions.find('.js-pill-container');
        $formSuggestionsPillContainer.html('');

        const $suggestionPills = [];

        $.each(fieldNameToSuggestionMap, (fieldName, suggestions) => {
            const $formGroup = $(`.form-group.${fieldName}`);

            if ($formGroup.length) {
                let $suggestionsContainer = $formGroup.find('.js-form-group-suggestions');

                if ($suggestionsContainer.length === 0) {
                    $suggestionsContainer = $('<div></div>', {
                        class: 'form-group-suggestions js-form-group-suggestions hidden',
                    });

                    $formGroup.append($suggestionsContainer);
                }

                let suggestionLabel = getEditableSuggestionsLabel();
                if (this.fieldNameToAutocomplete[fieldName]) {
                    suggestionLabel = getSuggestionsLabel();
                }
                const $suggestions = [
                    $('<span></span>', {
                        class: 'form-group-suggestions-label',
                        text: suggestionLabel,
                    }),
                ];

                const $pillContainer = this.$cardEl.find('.js-form-suggestions .js-pill-container');
                const shouldMarshalPills = $pillContainer.length !== 0;

                for (let i = 0; i < suggestions.length; i += 1) {
                    const suggestion = suggestions[i];
                    const attrs = {
                        class: 'suggestion js-suggestion',
                        text: suggestion.taxonomy || suggestion.display,
                    };

                    const dataAttrs = {
                        'data-field-name': fieldName,
                    };
                    $.each(suggestion, (k, v) => {
                        dataAttrs[`data-${k}`] = v;
                    });

                    if (shouldMarshalPills) {
                        const pill = new Pill({
                            dataAddOnType:
                                $.inArray(fieldName, ['colors', 'primary_color_code']) !== -1
                                    ? PillAddOnType.SWATCH
                                    : null,
                            value: suggestion,
                        });
                        pill.bind();
                        const $pillEl = pill.getEl();
                        $pillEl.addClass('js-suggestion').attr(dataAttrs);
                        this.bindSuggestionPill(pill);
                        $suggestionPills.push($pillEl);
                    }

                    $suggestions.push($('<span></span>', $.extend({}, attrs, dataAttrs)));
                }

                $suggestionsContainer.html($suggestions).toggleClass('hidden', !$suggestions.length);
            }
        });

        $formSuggestionsPillContainer.html($suggestionPills);
        $formSuggestions.toggleClass('hidden', $formSuggestionsPillContainer.is(':empty'));
    }
}

export default ProductUpdateCard;
